import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase.js';
import { CL } from './colors.js';
import { L } from './lang.js';
import { CustomerRow } from './CustomerRow.jsx';
import { NewCustomerForm } from './NewCustomerForm.jsx';

const FONT = "'Figtree', sans-serif";

const STATUS_ORDER = ['All', 'Trial', 'Grace', 'Paid', 'Expired', 'Cancelled'];
const FILTER_KEYS = { All: null, Trial: 'trial', Grace: 'grace', Paid: 'paid',
                      Expired: 'expired', Cancelled: 'cancelled' };

export function AdminDashboard() {
  const nav = useNavigate();

  const [stats, setStats]     = useState(null);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('All');
  const [expandedId, setExpandedId] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  const loadData = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { nav('/login', { replace: true }); return; }

    // Re-read the caller's `admins` row alongside the dashboard RPCs so a
    // mid-session revocation propagates within one poll cycle (≤ 60s). The
    // `admins_self_read` RLS policy returns only the caller's own row.
    const [
      { data: adminRow, error: aErr },
      { data: statsData, error: sErr },
      { data: rowsData, error: rErr },
    ] = await Promise.all([
      supabase.from('admins').select('user_id').eq('user_id', session.user.id).maybeSingle(),
      supabase.rpc('admin_stats'),
      supabase.rpc('admin_customer_overview'),
    ]);

    if (sErr?.status === 401 || rErr?.status === 401 || aErr?.status === 401) {
      nav('/login', { replace: true });
      return;
    }

    if (!adminRow) {
      await supabase.auth.signOut();
      nav('/unauthorized', { replace: true });
      return;
    }

    setStats(statsData?.[0] ?? null);
    setRows(rowsData ?? []);
    setLoading(false);
  }, [nav]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setAdminEmail(session.user.email);
    });
    loadData();
    const id = setInterval(loadData, 60000);
    return () => clearInterval(id);
  }, [loadData]);

  async function signOut() {
    await supabase.auth.signOut();
    nav('/login', { replace: true });
  }

  function toggleRow(id) {
    setExpandedId(prev => (prev === id ? null : id));
  }

  function selectFilter(label) {
    setFilter(label);
    setExpandedId(null);
  }

  function tileClick(label) {
    if (label === 'New this week') return;
    selectFilter(label === 'Total' ? 'All' : label);
  }

  const filtered = filter === 'All'
    ? rows
    : rows.filter(r => r.plan_status === FILTER_KEYS[filter]);

  const TILE_MAP = [
    { label: 'Total',         value: stats?.total         ?? '—', clickable: true  },
    { label: 'Trial',         value: stats?.trial         ?? '—', clickable: true  },
    { label: 'Grace',         value: stats?.grace         ?? '—', clickable: true  },
    { label: 'Paid',          value: stats?.paid          ?? '—', clickable: true  },
    { label: 'Expired',       value: stats?.expired       ?? '—', clickable: true  },
    { label: 'Cancelled',     value: stats?.cancelled     ?? '—', clickable: true  },
    { label: 'New this week', value: stats?.new_this_week ?? '—', clickable: false },
  ];

  const valueColor = (label) => {
    if (label === 'Trial')         return '#b07a28';
    if (label === 'Grace')         return CL.terra;
    if (label === 'Paid')          return CL.olive;
    if (label === 'New this week') return CL.sea;
    return CL.bark;
  };

  return (
    <div style={{ minHeight: '100vh', background: CL.sand, fontFamily: FONT }}>

      {/* ── Header ── */}
      <header style={{ background: CL.bark, color: CL.cream, height: 52,
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       padding: '0 32px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.06em',
                      display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill={CL.gold}>
            <polygon points="8,1 15,13 1,13"/>
          </svg>
          {L.appTitle}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
          <span style={{ color: CL.goldL, opacity: 0.85 }}>{adminEmail}</span>
          <button onClick={signOut}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
                     color: CL.cream, padding: '5px 12px', borderRadius: 5,
                     fontSize: 12, fontFamily: FONT, cursor: 'pointer', letterSpacing: '0.02em' }}>
            {L.signOut}
          </button>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 32px 60px' }}>

        {/* ── Stats strip ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: 10, marginBottom: 20 }}>
          {TILE_MAP.map(({ label, value, clickable }) => (
            <div key={label}
              onClick={() => clickable && tileClick(label)}
              style={{
                background: CL.cream, borderRadius: 8, padding: '14px 16px',
                textAlign: 'center', cursor: clickable ? 'pointer' : 'default',
                border: `2px solid ${(filter === label || (label === 'Total' && filter === 'All')) ? CL.gold : 'transparent'}`,
              }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                            textTransform: 'uppercase', color: CL.stone, marginBottom: 6 }}>
                {label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: valueColor(label) }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_ORDER.map(label => (
              <button key={label} onClick={() => selectFilter(label)}
                style={{
                  background: filter === label ? CL.bark : CL.cream,
                  border: `1px solid ${filter === label ? CL.bark : CL.goldL}`,
                  color: filter === label ? CL.cream : CL.bark,
                  padding: '6px 14px', borderRadius: 20, fontSize: 12,
                  fontWeight: 500, fontFamily: FONT, cursor: 'pointer',
                }}>
                {label}
              </button>
            ))}
          </div>
          <button data-testid="new-customer-btn" onClick={() => setShowNewForm(v => !v)}
            style={{ background: CL.gold, color: CL.bark, border: 'none',
                     padding: '8px 18px', borderRadius: 6, fontSize: 13,
                     fontWeight: 600, fontFamily: FONT, cursor: 'pointer' }}>
            {L.btnNew}
          </button>
        </div>

        {/* ── New customer form (inline, above table) ── */}
        {showNewForm && (
          <NewCustomerForm
            onClose={() => setShowNewForm(false)}
            onCreated={() => { setShowNewForm(false); loadData(); }}
          />
        )}

        {/* ── Table ── */}
        <div style={{ background: CL.cream, borderRadius: 10, overflow: 'hidden',
                      border: `1px solid ${CL.goldL}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: CL.sand, borderBottom: `1px solid ${CL.goldL}` }}>
                {[
                  { label: L.colHotel,    width: '30%' },
                  { label: L.colOwner,    width: '28%' },
                  { label: L.colStatus,   width: '14%' },
                  { label: L.colDays,     width: '10%', title: L.colDaysTip },
                  { label: L.colLastSeen, width: '18%' },
                ].map(col => (
                  <th key={col.label} title={col.title}
                    style={{ padding: '10px 16px', textAlign: 'left', width: col.width,
                             fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
                             textTransform: 'uppercase', color: CL.stone }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: CL.stone }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: CL.stone }}>
                    No customers match this filter.
                  </td>
                </tr>
              )}
              {!loading && filtered.map(row => (
                <CustomerRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={toggleRow}
                  onUpdated={loadData}
                />
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
